import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGoBack } from "../../hooks/useGoBack";
import { motion, AnimatePresence } from "framer-motion";

import { apiPost } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* Reuse the website auth theme already defined for Login */
import "./Login.scss";
import BrandLogo from "../../components/BrandLogo/BrandLogo";

/* --- Centralized SVG Icons (matches Login.jsx) --- */
const Icons = {
  Shield: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.2em"
      height="1.2em"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
  ChevronLeft: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2.5"
      width="1.2em"
      height="1.2em"
    >
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
};

/* --- Helper UI Components --- */

const AuthCard = ({ children }) => (
  <motion.div
    className="auth-card"
    initial={{ opacity: 0, y: 30, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

/* Branding visual for the left panel — themed around account recovery */
const RecoveryVisual = () => (
  <div className="core-engine">
    <svg viewBox="0 0 400 400" fill="none" className="engine-svg">
      <defs>
        <filter id="recovery-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="10" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="recovery-ring" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.06" />
        </linearGradient>
        <linearGradient id="lock-body" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#eef2ff" />
          <stop offset="100%" stopColor="#ffffff" />
        </linearGradient>
      </defs>

      {/* Orbiting ring */}
      <circle
        cx="200"
        cy="200"
        r="150"
        stroke="url(#recovery-ring)"
        strokeWidth="1.5"
        strokeDasharray="8 14"
        className="anim-spin"
      />

      {/* Soft connection arc */}
      <path
        d="M60 220 Q 200 80, 340 220"
        stroke="#e0e7ff"
        strokeWidth="2"
        fill="none"
        className="anim-float"
      />

      {/* Central padlock */}
      <g filter="url(#recovery-glow)">
        {/* Shackle */}
        <path
          d="M170 175 V 155 a30 30 0 0 1 60 0 V 175"
          stroke="#4f46e5"
          strokeWidth="6"
          strokeLinecap="round"
          fill="none"
          className="anim-pulse"
        />
        {/* Body */}
        <rect
          x="150"
          y="175"
          width="100"
          height="80"
          rx="16"
          fill="url(#lock-body)"
          stroke="#4f46e5"
          strokeWidth="2.5"
        />
        {/* Keyhole */}
        <circle cx="200" cy="210" r="7" fill="#4f46e5" />
        <rect x="197" y="214" width="6" height="16" rx="2" fill="#4f46e5" />
      </g>

      {/* Floating envelopes / tokens */}
      <g
        className="anim-float"
        style={{ animationDelay: "0.2s" }}
        transform="translate(78 96)"
      >
        <rect
          width="48"
          height="32"
          rx="6"
          fill="#ffffff"
          stroke="#4f46e5"
          strokeWidth="2"
        />
        <path
          d="M2 4 L24 20 L46 4"
          stroke="#4f46e5"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
      </g>

      <circle
        cx="310"
        cy="110"
        r="8"
        fill="#38bdf8"
        className="anim-float"
        style={{ animationDelay: "0.8s" }}
      />
      <circle
        cx="200"
        cy="330"
        r="10"
        fill="#a5b4fc"
        className="anim-float"
        style={{ animationDelay: "0.5s" }}
      />
      <circle
        cx="320"
        cy="290"
        r="6"
        fill="#4f46e5"
        className="anim-float"
        style={{ animationDelay: "1s" }}
      />
    </svg>

    <div className="engine-text">
      <h2>Account Recovery</h2>
      <p>Receive a secure reset code at your registered email.</p>
    </div>
  </div>
);

/* --- Main Component --- */

const ForgotPassword = () => {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const emailRef = useRef(null);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    let isValid = true;

    if (!email.trim()) {
      setEmailError("Email is required");
      isValid = false;
    } else if (!emailRegex.test(email.trim())) {
      setEmailError("Enter a valid email address");
      isValid = false;
    }

    if (!isValid) return;

    setIsSubmitting(true);
    setEmailError("");

    try {
      const resData = await apiPost("user", "/forgot-password", { email });

      if (resData.status === "success") {
        showNotification(resData.message || "Reset code sent", "success");

        setTimeout(() => {
          navigate("/otp-verification", {
            state: { email, type: "FORGOT" },
          });
        }, 1000);
      } else {
        showNotification(resData.message || "Error sending OTP", "error");
      }
    } catch (error) {
      console.error("Forgot Password Error:", error);
      showNotification(getErrorMessage(error), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-auth-container">
      <Link to="/" className="auth-brand" aria-label="Go to Doql homepage">
        <BrandLogo variant="header" />
      </Link>

      {/* Left Branding Panel */}
      <section className="left-branding">
        <RecoveryVisual />
      </section>

      {/* Right Auth Interface */}
      <main className="right-interface">
        <AnimatePresence>
          {notification && (
            <motion.div
              className={`notification-bar notify-${notificationType}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <Icons.Shield /> {notification}
            </motion.div>
          )}
        </AnimatePresence>

        <AuthCard>
          <button className="back-btn" onClick={goBack}>
            <Icons.ChevronLeft /> Back
          </button>

          <header className="header">
            <h1>Forgot Password?</h1>
            <p>Enter your work email and we’ll send a one-time reset code.</p>
          </header>

          <form
            onSubmit={handleForgotPassword}
            className="form-fields"
            noValidate
          >
            <div className="input-field">
              <div className="label-box">
                <label htmlFor="forgot-email">Work Email</label>
              </div>
              <div className="control-box">
                <input
                  id="forgot-email"
                  ref={emailRef}
                  type="email"
                  value={email}
                  placeholder="name@company.com"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError("");
                  }}
                  required
                />
              </div>
              {emailError && <div className="error-text">{emailError}</div>}
            </div>

            <div className="action-submit">
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Reset Code"}
              </button>
            </div>
          </form>

          <p className="onboarding-link">
            Remember your password? <Link to="/login">Back to Sign In</Link>
          </p>
        </AuthCard>
      </main>
    </div>
  );
};

export default ForgotPassword;
